import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "helper"];
  static values = {
    field: String,
    initialAnalysis: String
  };

  static ANALYZE_DELAY = 5000;
  static POLL_INTERVAL = 2000;
  static POLL_TIMEOUT = 80000;

  connect() {
    this.debounceTimer = null;
    this.hasAnalysis = !!this.initialAnalysisValue;
    this.pollingInterval = null;
    this.pollTimeout = null;

    if (this.hasAnalysis) {
      this.displayAnalysis(this.initialAnalysisValue);
    }
  }

  inputChanged() {
    if (this.hasAnalysis) return;

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (this.inputTarget.value.trim().length > 0) {
        this.analyze();
      }
    }, this.constructor.ANALYZE_DELAY);
  }

  blur() {
    if (this.hasAnalysis) return;

    clearTimeout(this.debounceTimer);
    if (this.inputTarget.value.trim().length > 0) {
      this.analyze();
    }
  }

  refresh(event) {
    event.preventDefault();
    this.analyze();
  }

  analyze() {
    if (this.isAnalyzing) return;
    this.isAnalyzing = true;

    this.helperTarget.classList.add("ai-suggestion-container");
    this.helperTarget.innerHTML = `<i class='fas fa-sparkles fa-sparkly'></i> ${I18n.t("js.ai.analyzing")}...`;

    const formData = new FormData();
    formData.append("field", this.fieldValue);

    // Grab values from all fields
    formData.append("title", this.valueFor("title"));
    formData.append("description", this.valueFor("description"));
    formData.append("series_title", this.valueFor("series_title"));

    const path = window.location.pathname;
    const episodeId = (path.match(/\/admin\/episodes\/(\d+)/) || [])[1] || null;

    // No episode yet on the new webinar page, so nothing to persist against
    if (episodeId) {
      formData.append("episode_id", episodeId);
    }

    fetch("/admin/ai/metadata_analyses", {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      }
    })
      .then(r => r.json())
      .then(json => {
        if (json.status === "queued" && json.job_id) {
          this.pollForResult(json.job_id);
        } else {
          this.isAnalyzing = false;
          this.helperTarget.innerHTML =
            `<i class="fas fa-sparkles"></i> <em>${I18n.t("js.ai.no_suggestions_available")}</em> ${this.refreshButton()}`;
        }
      })
      .catch(() => {
        this.isAnalyzing = false;
        this.helperTarget.innerHTML = "";
      });
  }

  pollForResult(jobId) {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
    if (this.pollTimeout) clearTimeout(this.pollTimeout);

    this.pollTimeout = setTimeout(() => {
      this.clearPolling();
      this.helperTarget.innerHTML = `<em>${I18n.t("js.ai.analysis_failed")}</em> ${this.refreshButton()}`;
    }, this.constructor.POLL_TIMEOUT);

    this.pollingInterval = setInterval(() => {
      fetch(`/admin/ai/metadata_analyses/${jobId}`)
        .then(r => r.json())
        .then(json => {
          if (json.status === "done") {
            this.clearPolling();
            this.displayAnalysis(json.text);
          } else if (json.status === "failed") {
            this.clearPolling();
            this.helperTarget.innerHTML = `<em>${I18n.t("js.ai.analysis_failed")}</em> ${this.refreshButton()}`;
          }
        })
        .catch(error => {
          console.error("Polling error:", error);
        });
    }, this.constructor.POLL_INTERVAL);
  }

  clearPolling() {
    clearInterval(this.pollingInterval);
    clearTimeout(this.pollTimeout);
    this.pollingInterval = null;
    this.pollTimeout = null;
    this.isAnalyzing = false;
  }

  displayAnalysis(text) {
    const safe = document.createElement("div");
    safe.textContent = text;

    this.helperTarget.classList.add("ai-suggestion-container");
    this.helperTarget.innerHTML =
      `<i class="fas fa-sparkles"></i>
       <span class="ai-suggestion-label">${I18n.t("js.ai.metadata_suggestion")}</span>
       <span class="ai-suggestion-text">${safe.innerHTML}</span>
       ${this.refreshButton()}`;
  }

  // helper to create refresh button HTML
  refreshButton() {
    const refreshText = I18n.t("js.ai.refresh");
    return `
      <button type="button" 
              class="btn btn-link ai-suggestion-refresh" 
              data-action="ai-metadata#refresh" 
              title="${refreshText}"
              aria-label="${refreshText}"
              style="text-decoration: none;">
        <i class="fas fa-redo-alt" aria-hidden="true"></i>
      </button>
    `;
  }

  // helper for grabbing cross-field values
  valueFor(field) {
    const el = document.querySelector(`[data-ai-field="${field}"] input, [data-ai-field="${field}"] textarea`);
    return el ? el.value : "";
  }

  disconnect() {
    clearInterval(this.pollingInterval);
    clearTimeout(this.pollTimeout);
    clearTimeout(this.debounceTimer);
  }
}
